/**
 * An access case, opened (WALLIYO-0131).
 *
 * Who is asking, what for, and the decision. It sits inside the case panel
 * rather than on a screen of its own: a request for a permission is a case,
 * and the claiming, the timeline and the audit around it are the ones cases
 * already have.
 *
 * The person is spelled out — their UUID, their roles, their teams — because
 * a decision made without knowing who is asking is the one that gets made
 * badly. Their team is context and nothing more: a team grants nothing, and
 * the grant lands on the person alone.
 */

import type { AccessRequest, SupportCaseDetail } from "@wallyo/contracts/console/cases";
import { useState } from "react";

import { decideAccessRequest } from "../../api/console-api";
import { useApi } from "../../api/context";
import { permissionName, t } from "../../i18n";
import { formatDate } from "../../i18n/format";
import { useStaff } from "../../shell/staff-context";
import { WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import { describeConfigFailure } from "../config-refusal";

const describeFailure = (error: unknown) => describeConfigFailure(error, {}, "features.stale");

export function AccessRequestPanel({ detail, onDecided }: { detail: SupportCaseDetail; onDecided: () => void }) {
  const request = detail.request;
  if (request === null) return null;
  return <Decide detail={detail} request={request} onDecided={onDecided} />;
}

function Decide({
  detail,
  request,
  onDecided,
}: {
  detail: SupportCaseDetail;
  request: AccessRequest;
  onDecided: () => void;
}) {
  const { client } = useApi();
  const { me } = useStaff();
  const [reason, setReason] = useState("");
  const save = useIntentWrite();

  // Nobody answers their own request. The server refuses it too; this is so
  // the buttons are dead rather than refused after pressing.
  const own = me.staffMember.staffMemberId === request.staffMemberId;
  const settled = detail.status === "closed";

  const answer = (granted: boolean) => {
    save.edited();
    void save.run(
      reason.trim().length < 3 ? t("features.save.reasonNeeded") : null,
      (idempotencyKey) =>
        decideAccessRequest(client, detail.supportCaseId, { granted, reason: reason.trim() }, { idempotencyKey }),
      onDecided,
    );
  };

  return (
    <section className="card access-request">
      <h2>{t("cases.access.wants")}</h2>
      <dl>
        <dt>{t("cases.access.wants")}</dt>
        <dd>{permissionName(request.settingId as never)}</dd>
        <dt>{t("teams.col.person")}</dt>
        <dd>
          {request.name} <span className="mono meta">{request.uuid}</span>
        </dd>
        <dt>{t("teams.col.roles")}</dt>
        <dd>{request.roles.length > 0 ? request.roles.join(", ") : "—"}</dd>
        <dt>{t("teams.title")}</dt>
        <dd>{request.teams.length > 0 ? request.teams.join(", ") : "—"}</dd>
        <dt>{t("cases.access.until")}</dt>
        <dd>{request.until === null ? "—" : formatDate(request.until, { dateStyle: "medium" })}</dd>
      </dl>
      <p className="meta">{t("cases.access.explain")}</p>

      {settled ? null : (
        <>
          <label className="save-reason">
            <span>{t("team.reason")}</span>
            <input type="text" value={reason} onChange={(event) => setReason(event.target.value)} />
          </label>
          <div className="access-actions">
            <button type="button" className="btn quiet" disabled={own || save.pending} onClick={() => answer(false)}>
              {t("cases.access.refuse")}
            </button>
            <button type="button" className="btn primary" disabled={own || save.pending} onClick={() => answer(true)}>
              {t("cases.access.grant")}
            </button>
          </div>
          {own ? <p className="value-note locked">{t("cases.access.own")}</p> : null}
          {save.problem ? <span className="subject-problem">{save.problem}</span> : null}
        </>
      )}
      {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={onDecided} /> : null}
    </section>
  );
}
